/**
 * Practice (org) branding — `BACKLOG.md` B4's "org brand + logo" item.
 *
 * `claude-kit/rules/invariants.md`'s "Commercial products let the customer brand it" is the
 * requirement this exists for: a paying practice sets its own colour scheme and logo for its own
 * users, distinct from — and taking precedence over — the individual accent picker
 * `web/src/theme/palette.ts`'s closed `ACCENTS` enum already gives every user. The logo is
 * different in kind, not just degree, per that same invariant: a user never overrides the
 * practice's logo under any configuration, only its colour, and only when the practice allows it.
 *
 * **Colour and logo are gated differently, on purpose (`BACKLOG.md` B4's white-label sub-item).** A
 * practice may set `brandColor` and `allowUserColorPicker` on any tier — this is ordinary product
 * packaging, matching `entitlements.ts`'s reasoning for every other commercial feature: nothing
 * about a colour choice is a certified capability or an interoperability element. Full logo
 * replacement is gated behind the `whiteLabel` commercial feature (enterprise-only,
 * `entitlements.ts`'s `COMMERCIAL_FEATURES_BY_TIER`) — the line most B2B SaaS products actually
 * draw, and consistent with `whiteLabel` already existing as an entry with nothing yet gated behind
 * it (`shared/src/entitlements.test.ts` covers the tier table; `server/src/auth/requireFeature.
 * test.ts` already exercises `requireFeature('whiteLabel')` as a real middleware gate, just not
 * mounted on any route until this file's own `server/src/orgs/brandingRoutes.ts`). A lower tier
 * that has not licensed `whiteLabel` cannot set a logo at all — `updateOrgLogoRequestSchema` is
 * validated the same regardless of tier, but the *route* it is submitted to
 * (`PUT /api/org/branding/logo`) is mounted behind `requireFeature('whiteLabel')`, so an unentitled
 * practice never reaches the schema at all and gets the standard 402 `not_entitled` response.
 *
 * **Storage: reuses `orgs.brandColor`/`orgs.logoUrl`/`orgs.allowUserColorPicker`
 * (`server/src/db/schema/platform.ts`), not a new table.** Those three columns have existed since
 * migration `0000` — added ahead of any consumer, with that table's own doc comment already naming
 * the invariant this file now implements ("A user may override colours but never the logo") — so
 * this feature needed no new migration.
 *
 * **`logoUrl` is an externally-hosted URL, not an upload through this app's own storage**, and that
 * is a deliberate scope decision rather than an oversight: `BACKLOG.md` C9 (document storage on S3 +
 * SSE-KMS, with magic-byte content verification and malware scanning) has not landed as of this
 * writing. Building a bespoke upload path for exactly one use case ahead of that general capability
 * would be the second, narrower storage mechanism `claude-kit/rules/invariants.md` warns against —
 * a practice admin points `logoUrl` at an image already hosted somewhere else (their own site, an
 * asset CDN) until C9 lands and a real upload flow can reuse it instead of duplicating it. `https`
 * only, to keep an `<img src>` render path from ever receiving a `javascript:` or unscoped `data:`
 * URI.
 *
 * **Contrast is not re-validated here.** `web/src/theme/guard.ts`'s `verifyTheme` already exists
 * specifically for "the day an arbitrary hex reaches it" (that file's own header comment) and is
 * exercised on every render, client-side, the moment a practice's `brandColor` is applied — a
 * colour that fails AA against either theme mode is rejected and rolled back there, with a visible
 * notice, rather than silently shipped. Duplicating that WCAG math into a second, server-side gate
 * here would be a second implementation of the same decision (`claude-kit/rules/invariants.md`,
 * "favor the general, reusable solution") for no reduction in risk — a malformed value still can
 * never reach a user's screen unchecked, only ever exist, briefly, as a stored value the guard has
 * not yet had a chance to reject. This schema validates *format* only (a well-formed `#rrggbb` hex),
 * which is the one property a client-side contrast check cannot substitute for.
 */

import { z } from 'zod';

/** `#rrggbb`, case-insensitive. Matches `web/src/theme/contrast.ts`'s `HEX_LONG` pattern — the
 * short `#rgb` form is not accepted here, since this is a value a practice admin fills into a form
 * (a colour picker or a typed hex), not one echoed back from a `getComputedStyle()` read the way
 * `contrast.ts`'s own parser has to tolerate. */
export const BRAND_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export const brandColorSchema = z
  .string()
  .regex(BRAND_COLOR_PATTERN, 'Expected a hex colour like #1c716b')
  .nullable();

/** `null` clears the logo, falling back to the product's own default mark. */
export const orgLogoUrlSchema = z
  .url({ protocol: /^https$/, error: 'Expected an https:// URL' })
  .max(2048)
  .nullable();

/** The practice's current branding, as read by any signed-in member (`GET /api/org/branding` — no
 * capability gate, matching `orgAiSettingsRoutes.ts`'s own `GET` precedent: knowing your own
 * practice's colours and logo is not sensitive, and every client needs it to render the theme,
 * not just the admin who can change it). */
export const orgBrandingSchema = z.strictObject({
  brandColor: brandColorSchema,
  logoUrl: orgLogoUrlSchema,
  /** Whether a member may override `brandColor` with their own accent pick
   * (`web/src/theme/palette.ts`'s `ACCENTS`). The logo has no equivalent flag — per the workspace
   * invariant, a user never overrides the logo, so there is nothing to toggle. */
  allowUserColorPicker: z.boolean(),
});
export type OrgBranding = z.infer<typeof orgBrandingSchema>;

export const orgBrandingResponseSchema = z.strictObject({ branding: orgBrandingSchema });
export type OrgBrandingResponse = z.infer<typeof orgBrandingResponseSchema>;

/** `PUT /api/org/branding` — `org:manage_settings`, any tier. No `logoUrl` field: that is
 * `updateOrgLogoRequestSchema` below, submitted to its own `whiteLabel`-gated route. */
export const updateOrgBrandingRequestSchema = z.strictObject({
  brandColor: brandColorSchema,
  allowUserColorPicker: z.boolean(),
});
export type UpdateOrgBrandingRequest = z.infer<typeof updateOrgBrandingRequestSchema>;

/** `PUT /api/org/branding/logo` — `org:manage_settings` **and** `requireFeature('whiteLabel')`. */
export const updateOrgLogoRequestSchema = z.strictObject({
  logoUrl: orgLogoUrlSchema,
});
export type UpdateOrgLogoRequest = z.infer<typeof updateOrgLogoRequestSchema>;
